import { z } from "zod";
import { FACTIONS, GALAXY, NODES } from "../core/data";
import { nodesOf } from "../core/world";
import { shipMax, combatBastion } from "../core/combat";
import type { GameState } from "../core/types";
const n = (max = 1e9) => z.number().finite().min(0).max(max),
  i = (max = 1e9) => n(max).int(),
  f = z.enum([
    "alliance",
    "greys",
    "reptiloids",
    "okalani",
    "xilur",
    "nocturn",
  ]),
  id = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/),
  sys = z.enum(GALAXY.nodes.map((n) => n.id) as [string, ...string[]]),
  safe = z
    .string()
    .max(160)
    .refine((s) => !/[<>\u0000-\u0008]/.test(s)),
  role = z.enum(["vanguard", "guardian", "navigator"]),
  shipClass = z.enum(["scout", "cruiser", "dreadnought"]),
  stance = z.enum(["offensive", "neutral", "defensive"]),
  admiral = z.object({ role, xp: i(1e7) }).strict(),
  ship = z
    .object({ id, type: shipClass, hull: i(1000), shields: i(1000) })
    .strict(),
  effect = z
    .object({ faction: f, expires: i(100001) })
    .strict()
    .nullable(),
  tech = z
    .object({
      weapons: i(3),
      shields: i(3),
      propulsion: i(3),
      infrastructure: i(3),
    })
    .strict(),
  intel = z
    .object({
      owner: f.nullable(),
      seen: i(100000),
      bastion: i(3).nullable(),
      fleetCount: i(18).nullable(),
      ships: z.array(shipClass).max(126).nullable(),
    })
    .strict();
const faction = z
    .object({
      id: f,
      credits: i(),
      rp: i(),
      charge: i(200),
      tech,
      eliminated: z.boolean(),
      shortage: i(3),
      turnCount: i(100001),
      specialUsed: z.boolean(),
      sensorUntil: i(100003),
      discovered: z.array(sys).max(36),
      intel: z.record(sys, intel),
    })
    .strict(),
  system = z
    .object({
      id: sys,
      owner: f.nullable(),
      mine: i(3),
      research: i(3),
      bastion: i(3),
      grid: i(2),
      builtTurn: i(100001),
      guards: z.array(ship).max(7),
      guardStyle: f,
      barrier: effect,
      sabotage: effect,
      ion: effect,
      bastionHull: i(1000).nullable(),
      bastionShields: i(1000).nullable(),
    })
    .strict(),
  fleet = z
    .object({
      id,
      faction: f,
      system: sys,
      ships: z.array(ship).min(1).max(7),
      admiral,
      moves: i(4),
      slipUsedTurn: i(100001),
    })
    .strict();
const unit = z
    .object({
      id,
      type: z.enum(["scout", "cruiser", "dreadnought", "bastion"]),
      faction: f,
      hull: i(2000),
      shields: i(2000),
      maxHull: i(2000).min(1),
      maxShields: i(2000).min(1),
      attack: i(1000),
      accuracy: n(1),
      accuracyBonus: n(0.2),
      evasion: n(0.5),
      initiative: i(20),
      resistance: n(0.6),
    })
    .strict()
    .refine((u) => u.hull <= u.maxHull && u.shields <= u.maxShields),
  side = z
    .object({
      faction: f.nullable(),
      style: f,
      fleetId: id.nullable(),
      admiral: admiral.nullable(),
      stance,
      units: z.array(unit).min(1).max(8),
    })
    .strict(),
  event = z
    .object({
      round: i(24),
      type: z.enum(["hit", "miss", "destroy", "retreat", "end"]),
      source: safe,
      target: safe,
      damage: i(2000),
      shields: i(2000),
      hull: i(2000),
    })
    .strict(),
  outcome = z.enum(["active", "attacker", "defender", "retreat"]);
const battle = z
  .object({
    id,
    system: sys,
    origin: sys,
    attacker: side,
    defender: side,
    rng: i(4294967295),
    seed: i(4294967295),
    round: i(24),
    status: outcome,
    events: z.array(event).max(1200),
    initialAttacker: i(7),
    initialDefender: i(7),
    attackerValue: i(10000),
    defenderValue: i(10000),
    nebula: z.boolean(),
    nocturnTriggered: z.boolean(),
    difficulty: z.enum(["easy", "normal", "hard"]),
  })
  .strict();
const report = z
    .object({
      id,
      turn: i(100000),
      system: sys,
      attacker: f,
      defender: f.nullable(),
      outcome,
      rounds: i(24),
      lostAttack: i(7),
      lostDefense: i(7),
      creditsLost: i(20000),
      attackerXp: i(1000),
      defenderXp: i(1000),
      attackerRanks: z.tuple([i(5).min(1), i(5).min(1)]),
      defenderRanks: z.tuple([i(5).min(1), i(5).min(1)]),
      captured: z.boolean(),
      surrendered: z.array(f).max(6),
      engagement: battle.nullable(),
    })
    .strict(),
  ledger = z
    .object({
      turn: i(100000),
      faction: f,
      key: safe,
      amount: z.number().finite().min(-1e9).max(1e9),
      currency: z.enum(["credits", "rp", "charge", "none"]),
      ref: safe,
    })
    .strict();
const galaxy = z
  .object({
    generator: z.literal("meridian-v2"),
    nodes: z
      .array(
        z
          .object({
            id: sys,
            name: z.object({ en: safe, de: safe }).strict(),
            x: i(1000),
            y: i(760),
            stars: i(4).min(1),
            type: z.enum(["homeworld", "frontier", "nexus"]),
            startOwner: f.nullable(),
            sector: z.enum([
              "alliance",
              "greys",
              "reptiloids",
              "okalani",
              "xilur",
              "nocturn",
              "core",
            ]),
            special: z.enum(["global-reveal", "deep-scan"]).nullable(),
            visualVariant: z.enum([
              "home",
              "temperate",
              "nebula",
              "anomaly",
              "ice",
              "rock",
              "gas",
              "ocean",
              "volcanic",
              "dark",
              "desert",
            ]),
            neighbors: z.array(sys).min(1).max(12),
          })
          .strict(),
      )
      .length(36),
  })
  .strict();
export const saveSchema = z
  .object({
    schemaVersion: z.literal(1),
    version: z.enum(["1.0.0", "1.1.0", "1.2.0"]),
    galaxy: galaxy.optional(),
    seed: z
      .string()
      .min(1)
      .max(80)
      .regex(/^[\p{L}\p{N} .:_-]+$/u),
    rng: i(4294967295),
    nextId: i(),
    player: f,
    difficulty: z.enum(["easy", "normal", "hard"]),
    turn: i(100000).min(1),
    order: z.array(f).min(2).max(6),
    actorIndex: i(5),
    actorActions: i(10000),
    phase: z.enum(["map", "battle", "victory", "defeat"]),
    factions: z
      .object(Object.fromEntries(FACTIONS.map((f) => [f, faction])))
      .strict(),
    systems: z
      .object(Object.fromEntries(GALAXY.nodes.map((n) => [n.id, system])))
      .strict(),
    fleets: z.array(fleet).max(18),
    battle: battle.nullable(),
    reports: z.array(report).max(200),
    ledger: z.array(ledger).max(2400),
    tutorial: z.number().int().min(-1).max(20),
  })
  .strict();
export const MAX_SAVE_BYTES = 5 * 1024 * 1024;
export function validateSave(input: unknown): GameState {
  const g = saveSchema.parse(input) as unknown as GameState,
    check = (x: unknown, s: string) => {
      if (!x) throw new Error(s);
    };
  check(g.version === "1.0.0" || g.galaxy, "Missing generated galaxy");
  if (g.galaxy) {
    const nodes = nodesOf(g),
      mapIds = g.galaxy.nodes.map((n) => n.id);
    check(new Set(mapIds).size === 36, "Duplicate galaxy system");
    for (const lang of ["en", "de"] as const)
      check(
        new Set(g.galaxy.nodes.map((n) => n.name[lang])).size === 36,
        "Duplicate galaxy name",
      );
    for (const n of g.galaxy.nodes) {
      check(
        n.startOwner === NODES[n.id].startOwner &&
          n.type === NODES[n.id].type &&
          n.special === NODES[n.id].special,
        "Invalid galaxy identity",
      );
      check(!n.startOwner || n.stars === 4, "Invalid homeworld rating");
      check(
        new Set(n.neighbors).size === n.neighbors.length,
        "Duplicate galaxy route",
      );
      for (const id of n.neighbors)
        check(
          id !== n.id && nodes[id]?.neighbors.includes(n.id),
          "Invalid galaxy route",
        );
    }
    const seen = new Set([mapIds[0]]),
      queue = [mapIds[0]];
    while (queue.length)
      for (const id of nodes[queue.shift()!]!.neighbors)
        if (!seen.has(id)) {
          seen.add(id);
          queue.push(id);
        }
    check(seen.size === 36, "Disconnected galaxy");
  }
  check(
    new Set(g.order).size === g.order.length &&
      g.order[0] === g.player &&
      g.actorIndex < g.order.length,
    "Invalid turn order",
  );
  check((g.phase === "battle") === !!g.battle, "Battle phase mismatch");
  if (g.phase === "victory")
    check(
      !g.factions[g.player].eliminated &&
        g.order
          .filter((f) => f !== g.player)
          .every((f) => g.factions[f].eliminated),
      "Invalid victory",
    );
  if (g.phase === "defeat")
    check(g.factions[g.player].eliminated, "Invalid defeat");
  if (g.phase === "map" || g.phase === "battle")
    check(!g.factions[g.player].eliminated, "Eliminated player");
  const ids = new Set<string>(),
    unique = (id: string) => {
      check(!ids.has(id), "Duplicate entity");
      ids.add(id);
    };
  for (const f of FACTIONS) {
    check(g.factions[f].id === f, "Faction mismatch");
    check(
      g.order.includes(f) || g.factions[f].eliminated,
      "Inactive civilization flag",
    );
    const fs = g.fleets.filter((fl) => fl.faction === f);
    check(
      fs.length <= 3 &&
        new Set(fs.map((fl) => fl.admiral.role)).size === fs.length,
      "Fleet/commander limit",
    );
    check(
      new Set(g.factions[f].discovered).size ===
        g.factions[f].discovered.length,
      "Discovery duplicate",
    );
  }
  for (const [id, s] of Object.entries(g.systems)) {
    check(id === s.id && NODES[id], "Invalid system");
    check(!(s.mine && s.research), "Economic slot conflict");
    check(!s.grid || s.bastion, "Grid without bastion");
    check(
      !s.owner ||
        (g.order.includes(s.owner) && !g.factions[s.owner].eliminated),
      "Invalid owner",
    );
    check(!s.owner || !s.guards.length, "Guards at owned system");
    check(
      (s.bastionHull === null) === (s.bastionShields === null),
      "Partial station health",
    );
    if (!s.bastion)
      check(
        s.bastionHull === null && s.bastionShields === null,
        "Health without station",
      );
    else if (s.bastionHull !== null) {
      const max = combatBastion(
        "station",
        s.owner ?? s.guardStyle,
        s.bastion,
        s.grid,
      );
      check(
        s.bastionHull > 0 &&
          s.bastionHull <= max.maxHull &&
          s.bastionShields !== null &&
          s.bastionShields <= max.maxShields,
        "Invalid station health",
      );
    }
    for (const u of s.guards) {
      unique(u.id);
      const m = shipMax(u.type, s.guardStyle);
      check(
        u.hull > 0 && u.hull <= m.hull && u.shields <= m.shields,
        "Invalid guard health",
      );
    }
  }
  for (const fl of g.fleets) {
    unique(fl.id);
    check(
      g.order.includes(fl.faction) && !g.factions[fl.faction].eliminated,
      "Inactive fleet",
    );
    for (const u of fl.ships) {
      unique(u.id);
      const m = shipMax(
        u.type,
        fl.faction,
        g.factions[fl.faction].tech.shields,
      );
      check(
        u.hull > 0 && u.hull <= m.hull && u.shields <= m.shields,
        "Invalid ship health",
      );
    }
  }
  if (g.battle) {
    const b = g.battle;
    check(
      g.fleets.some(
        (fl) =>
          fl.id === b.attacker.fleetId && fl.faction === b.attacker.faction,
      ),
      "Invalid attacker",
    );
    check(nodesOf(g)[b.system].neighbors.includes(b.origin), "Invalid route");
    const units = [...b.attacker.units, ...b.defender.units].map((u) => u.id),
      valid = new Set(units);
    check(valid.size === units.length, "Duplicate unit");
    for (const e of b.events)
      check(
        (!e.source || valid.has(e.source)) &&
          (!e.target || valid.has(e.target)),
        "Invalid event reference",
      );
  }
  return g;
}
export function parseSave(text: string) {
  if (new TextEncoder().encode(text).byteLength > MAX_SAVE_BYTES)
    throw new Error("Save exceeds 5 MB");
  return validateSave(JSON.parse(text));
}
